import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonExplainModule } from '../../../../common-explain/common-explain.module';
import { DocumentProcessingStepEnum } from '../../../models/response-project-document';
import { getDocumentTypeColor } from '../document-types';

export type DocumentIdentityIconState =
  | 'default'
  | 'uploading'
  | 'extracting'
  | 'categorizing'
  | 'indexing'
  | 'just-categorized'
  | 'archive';

@Component({
  selector: 'app-document-identity',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, NgbTooltipModule, CommonExplainModule],
  templateUrl: './document-identity.component.html',
  styleUrls: ['./document-identity.component.scss'],
})
export class DocumentIdentityComponent {
  readonly DocumentProcessingStepEnum = DocumentProcessingStepEnum;

  @Input({ required: true }) name = '';
  @Input() subtypeName?: string | null;
  @Input() fileSize?: number;
  @Input() uploaderName?: string | null;
  @Input() updatedAt?: string | null;
  @Input() usedCount?: number;
  @Input() showMeta = true;
  @Input() showUncategorized = false;
  @Input() metaVariant: 'default' | 'library' = 'default';
  @Input() inLibraryLabel: string | null = null;
  @Input() addToLibraryLabel: string | null = null;
  @Input() libraryChecking = false;
  @Input() libraryCheckingLabel: string | null = null;
  @Input() processingStep: DocumentProcessingStepEnum | null = null;
  @Input() uploadProgress: number | null = null;
  @Input() isArchive = false;
  @Input() justCategorized = false;

  readonly libraryAction = output<void>();

  get isUncategorized(): boolean {
    return this.showUncategorized && !this.subtypeName && !this.isProcessing && !this.justCategorized;
  }

  get isProcessing(): boolean {
    return (
      !!this.processingStep &&
      [
        DocumentProcessingStepEnum.IMPORTING,
        DocumentProcessingStepEnum.UPLOADING,
        DocumentProcessingStepEnum.EXTRACTING,
        DocumentProcessingStepEnum.CATEGORIZING,
        DocumentProcessingStepEnum.INDEXING,
      ].includes(this.processingStep)
    );
  }

  get iconState(): DocumentIdentityIconState {
    if (this.processingStep === DocumentProcessingStepEnum.EXTRACTING) return 'extracting';
    if (this.processingStep === DocumentProcessingStepEnum.CATEGORIZING) return 'categorizing';
    if (this.processingStep === DocumentProcessingStepEnum.INDEXING) return 'indexing';
    if (this.justCategorized) return 'just-categorized';
    if (this.isArchive) return 'archive';
    if (this.processingStep === DocumentProcessingStepEnum.UPLOADING) return 'uploading';
    return 'default';
  }

  get color(): string {
    return getDocumentTypeColor(this.subtypeName);
  }

  get formattedDate(): string | null {
    if (!this.updatedAt) return null;
    const date = new Date(this.updatedAt);
    if (isNaN(date.getTime())) return this.updatedAt;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  get fileSizeKey(): string | null {
    if (this.fileSize == null) return null;
    if (this.fileSize < 1024) return 'tenders.library.file-size.bytes';
    if (this.fileSize < 1024 * 1024) return 'tenders.library.file-size.kb';
    return 'tenders.library.file-size.mb';
  }

  get fileSizeParam(): string | number | null {
    if (this.fileSize == null) return null;
    if (this.fileSize < 1024) return this.fileSize;
    if (this.fileSize < 1024 * 1024) return (this.fileSize / 1024).toFixed(0);
    return (this.fileSize / (1024 * 1024)).toFixed(1);
  }
}
